import React, { useState } from 'react';
import { translate } from '../../Services/translation';
import { post } from '../../Services/http';
import Checkbox from '../Inputs/Checkbox';
import Icon from '../Icon';
import { trackingServiceNewsLetterEvent } from '../../Services/Tracking.service';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import useIsStoreScreen from '../../Hooks/useIsStoreScreen';
import clsx from 'clsx';

interface newsletterRegistrationProps {
  termsUrl: string;
}

const NewsletterRegistration = ({
  termsUrl,
}: newsletterRegistrationProps) => {
  const { currency } = useTypedSelector((state) => state.cart);
  const [email, setEmail] = useState('');
  const [registrationError, setRegistrationError] = useState('');
  const [isTryingToSubscribe, setIsTryingToSubscribe] = useState(false);
  const [hasApproved, setHasApproved] = useState(false);
  const [registrationSuccessful, setRegistrationSuccessful] =
    useState(false);
  const handleEmailChange = (e) => {
    setRegistrationSuccessful(false);
    setEmail(e.target.value);
  };
  const isStoreScreen = useIsStoreScreen();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasApproved) {
      setRegistrationError(translate('newsletter.please-approve-terms'));
      return;
    }
    if (registrationSuccessful || isTryingToSubscribe) {
      return;
    }
    setIsTryingToSubscribe(true);
    setRegistrationError('');

    post('/api/newsletter/subscribe', { email })
      .then((response) => {
        if (response.ok) {
          setRegistrationSuccessful(true);
          trackingServiceNewsLetterEvent(currency);
        } else {
          setRegistrationSuccessful(false);
          console.error('Failed to subscribe to newsletter.');
          setRegistrationError(
            translate('newsletter.registration-failed')
          );
        }
      })
      .catch((error) => {
        setRegistrationSuccessful(false);
        setRegistrationError(translate('newsletter.registration-failed'));
        console.error(
          'An error occurred while subscribing to newsletter:',
          error
        );
      })
      .finally(() => {
        setIsTryingToSubscribe(false);
        setEmail('');
      });
  };

  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center gap-0 xl:items-end',
        isStoreScreen &&
          'py-[1.875rem] md:py-[2.125rem] lg:items-center lg:py-[2.625rem]'
      )}
    >
      <h6 className="h2 mb-1.5 w-full max-w-[1024px] font-normal text-gray-700 lg:max-w-[768px]">
        {translate('newsletter-registration.title')}
      </h6>
      <div className="mb-4 w-full max-w-[1024px] text-lg text-gray-500 lg:max-w-[768px]">
        {translate('newsletter-registration.description')}
      </div>
      <form
        className="relative w-full max-w-[1024px] lg:max-w-[768px]"
        onSubmit={handleSubmit}
      >
        <input
          className="leading-large placeholder:h4 mb-3 w-full border-b border-gray-200 bg-transparent py-3 pr-4 text-lg text-gray-900 placeholder:text-gray-400 focus:bg-white focus:px-4 active:bg-white active:px-4 xl:py-4"
          type="text"
          onChange={handleEmailChange}
          placeholder={
            registrationSuccessful
              ? translate('newsletter.registration-success')
              : translate('newsletter.email-placeholder')
          }
          value={email}
          disabled={registrationSuccessful}
          required
        />
        <div className="flex items-center">
          <Checkbox
            inputSize={'small'}
            onChange={(e) => setHasApproved(e.target.checked)}
            label={<>{translate('newsletter.i-approve')} </>}
            wrapperClassName="[&_span]:!text-gray-700"
          />
          <a
            className="text-md text-gray-700 underline hover:opacity-80"
            href={termsUrl}
          >
            {translate('newsletter.terms')}
          </a>
        </div>
        {registrationError && (
          <div className="leading-large mt-2 text-sm text-error-500">
            {registrationError}
          </div>
        )}
        {registrationSuccessful && (
          <div className="leading-large mt-2 text-sm text-success-500">
            {translate('newsletter.registration-success')}
          </div>
        )}
        <button
          className="absolute right-[4px] top-[4px] z-[1] flex h-10 w-10 items-center justify-center rounded-sm bg-transparent text-gray-700 hover:text-gray-500 disabled:cursor-not-allowed xl:h-12 xl:w-12"
          disabled={registrationSuccessful}
          type="submit"
        >
          {registrationSuccessful ? (
            <Icon className="h-7 w-7 text-gray-700" name="check" />
          ) : isTryingToSubscribe ? (
            <Icon className="h-7 w-7 text-gray-700" name="refresh" />
          ) : (
            <Icon className="h-7 w-7 text-gray-700" name="arrow-right" />
          )}
        </button>
      </form>
    </div>
  );
};

export default NewsletterRegistration;
